import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const EMPTY = { type: "stipend", amount: "", month: "", payout_date: "", status: "Pending", notes: "" };

export default function LedgerEntryFormDialog({ open, onOpenChange, employeeId, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async () => {
    if (!form.amount || !form.month) {
      toast.error("Amount and month are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/employees/${employeeId}/ledger`, {
        ...form, employee_id: employeeId, amount: parseFloat(form.amount), payout_date: form.payout_date || null,
      });
      toast.success("Ledger entry recorded");
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="ledger-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add Ledger Entry</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="ledger-form-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="stipend">Stipend</SelectItem>
                  <SelectItem value="salary">Salary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Amount (₹)</Label>
              <Input data-testid="ledger-form-amount-input" type="number" value={form.amount} onChange={set("amount")} placeholder="45000" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Month (YYYY-MM)</Label>
              <Input data-testid="ledger-form-month-input" value={form.month} onChange={set("month")} placeholder="2026-03" />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="ledger-form-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Payout Date</Label>
            <Input data-testid="ledger-form-payout-date-input" type="date" value={form.payout_date} onChange={set("payout_date")} />
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Input data-testid="ledger-form-notes-input" value={form.notes} onChange={set("notes")} placeholder="Optional notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ledger-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="ledger-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : "Add Entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
